import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { searchContext } from '../../context/Context';
import ManageGrid from '../../modules/search/searchGrid/components/table/ManageGrid';
import {
  useMappingStackStore,
  useCurrentMappingLevel,
  useMappingStackBreadcrumb,
} from '../../store/mappingStackStore';
import type { MappingLevel } from '../../types/mappingDrillDown';
import { MappingBreadcrumb } from './MappingBreadcrumb';
import { mapItemsToSearchItems } from '../Table/types/index';
import { ColumnVisibilityModels } from '../Table/ColumnVisibilityModels';
import {
  useMappingSidebarColumn,
  type MappingSidebarColumnState,
} from '../../context/MappingSidebarColumnContext';
import { isEvidenceOnlyMapping, isSummaryOnlyMapping } from '../../config/mappingRules';
import { MappingDrillDownProvider } from '../../context/MappingDrillDownContext';
import { MappedGeneInfoButton } from './MappedGeneInfoButton';
import { MAPPING_URL_PARAM } from '../../utils/mappingUrlState';
import { getDefaultEvidenceFiltersForMapping } from '../../utils/mappingDefaultEvidenceFilters';

function toManageGridMappingContent(level: MappingLevel) {
  if (!level.mappingContent) return undefined;
  return {
    ...level.mappingContent,
    isActive: true,
  };
}

function DrillDownMappingView({
  level,
  view,
  source,
  tab,
  panelTab,
  setSearchItems,
  isInfoSidebarOpen,
  setIsInfoSidebarOpen,
  dispatch,
  registerSidebarColumnState,
  onOpenEntityInfo,
}: {
  level: MappingLevel;
  view: string;
  source: string;
  tab: string;
  panelTab?: string;
  setSearchItems: (items: any[]) => void;
  isInfoSidebarOpen: boolean;
  setIsInfoSidebarOpen: (v: boolean) => void;
  dispatch: any;
  registerSidebarColumnState: (s: MappingSidebarColumnState | null) => void;
  onOpenEntityInfo?: (item: any) => void;
}) {
  const mappingContent = level.mappingContent!;
  const keyBase = `mapping-${level.id}-${mappingContent.columVisibilityModelKey}`;
  const evidenceOnly = isEvidenceOnlyMapping(mappingContent.name);
  const summaryOnly = isSummaryOnlyMapping(mappingContent.name);
  const [activePanel, setActivePanel] = useState<'summary' | 'evidence'>(() =>
    evidenceOnly ? 'evidence' : 'summary',
  );

  const mappingItems = mappingContent.mappingItems ?? [];
  const hasFixedDisease = mappingItems.some((i: any) => i?.filterParam === 'disease');
  const hasFixedGene = mappingItems.some((i: any) => i?.filterParam === 'gene_ncbi_id');

  function applyFixedEntityDefaultColumnHiding(model: Record<string, boolean> | undefined) {
    if (!model) return model;
    const next = { ...model };

    if (hasFixedDisease) {
      Object.keys(next).forEach(key => {
        if (key.startsWith('disease')) next[key] = false;
      });
    }

    if (hasFixedGene) {
      Object.keys(next).forEach(key => {
        if (key.startsWith('gene')) next[key] = false;
      });
    }

    return next;
  }

  const defaultSummaryModel = useMemo(
    () =>
      applyFixedEntityDefaultColumnHiding(
        ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, false),
      ),
    [mappingContent.columVisibilityModelKey, hasFixedDisease, hasFixedGene],
  );
  const defaultEvidenceModel = useMemo(() => {
    const base = applyFixedEntityDefaultColumnHiding(
      ColumnVisibilityModels.getByMappingName(mappingContent.columVisibilityModelKey, true),
    );
    const selectedReferenceType = (mappingContent as { selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL' })
      .selectedReferenceType;

    if (!base) return base;
    if (selectedReferenceType === 'PMID' || selectedReferenceType === 'NCTID') {
      return { ...base, reference_type: true };
    }
    return base;
  }, [mappingContent, mappingContent.columVisibilityModelKey, hasFixedDisease, hasFixedGene]);

  const [summaryColumnModel, setSummaryColumnModel] = useState<Record<string, boolean> | undefined>(
    () => defaultSummaryModel ?? undefined,
  );
  const [summaryColumnOrder, setSummaryColumnOrder] = useState<string[] | undefined>(() =>
    defaultSummaryModel ? Object.keys(defaultSummaryModel).filter(k => defaultSummaryModel[k]) : undefined,
  );
  const [evidenceColumnModel, setEvidenceColumnModel] = useState<Record<string, boolean> | undefined>(
    () => defaultEvidenceModel ?? undefined,
  );
  const [evidenceColumnOrder, setEvidenceColumnOrder] = useState<string[] | undefined>(() =>
    defaultEvidenceModel ? Object.keys(defaultEvidenceModel).filter(k => defaultEvidenceModel[k]) : undefined,
  );

  useEffect(() => {
    setSummaryColumnModel(defaultSummaryModel ?? undefined);
    setSummaryColumnOrder(
      defaultSummaryModel ? Object.keys(defaultSummaryModel).filter(k => defaultSummaryModel[k]) : undefined,
    );
    setEvidenceColumnModel(defaultEvidenceModel ?? undefined);
    setEvidenceColumnOrder(
      defaultEvidenceModel
        ? Object.keys(defaultEvidenceModel).filter(k => defaultEvidenceModel[k])
        : undefined,
    );
  }, [defaultSummaryModel, defaultEvidenceModel]);

  useEffect(() => {
    if (evidenceOnly) setActivePanel('evidence');
    if (summaryOnly) setActivePanel('summary');
  }, [evidenceOnly, summaryOnly]);

  useEffect(() => {
    const model = activePanel === 'summary' ? summaryColumnModel : evidenceColumnModel;
    const order = activePanel === 'summary' ? summaryColumnOrder : evidenceColumnOrder;
    const setModel = activePanel === 'summary' ? setSummaryColumnModel : setEvidenceColumnModel;
    const setOrder = activePanel === 'summary' ? setSummaryColumnOrder : setEvidenceColumnOrder;
    if (model != null) {
      registerSidebarColumnState({
        columnVisibilityModel: model,
        setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) =>
          setModel(prev => updater(prev ?? {})),
        columnOrder: order ?? undefined,
        setColumnOrder: (orderList: string[]) => setOrder(orderList),
        columnPanelToggle:
          evidenceOnly || summaryOnly ? undefined : { active: activePanel, onChange: setActivePanel },
      });
    }
    return () => registerSidebarColumnState(null);
  }, [
    activePanel,
    summaryColumnModel,
    evidenceColumnModel,
    summaryColumnOrder,
    evidenceColumnOrder,
    registerSidebarColumnState,
    evidenceOnly,
    summaryOnly,
  ]);

  const contextItems = (() => {
    const items = mappingContent.mappingItems as Array<{
      name?: string;
      id?: string;
      filterParam?: string;
      filterValue?: string;
    }>;
    return items?.filter(i => i?.name) ?? [];
  })();

  const manageGridMappingContent = useMemo(() => toManageGridMappingContent(level), [level]);
  const searchItemsMapped = useMemo(
    () => mapItemsToSearchItems(mappingContent.mappingItems),
    [mappingContent.mappingItems],
  );
  const defaultEvidenceFilter = useMemo(
    () => getDefaultEvidenceFiltersForMapping(mappingContent),
    [mappingContent],
  );

  const handleSetSummaryColumnVisibilityModel = useCallback(
    (updater: (prev: Record<string, boolean>) => Record<string, boolean>) =>
      setSummaryColumnModel(prev => updater(prev ?? {})),
    [],
  );
  const handleSetSummaryColumnOrder = useCallback((order: string[]) => setSummaryColumnOrder(order), []);
  const handleSetEvidenceColumnVisibilityModel = useCallback(
    (updater: (prev: Record<string, boolean>) => Record<string, boolean>) =>
      setEvidenceColumnModel(prev => updater(prev ?? {})),
    [],
  );
  const handleSetEvidenceColumnOrder = useCallback((order: string[]) => setEvidenceColumnOrder(order), []);

  const drilldownSummaryEvidenceToggle = summaryOnly
    ? {
        showSummary: true as const,
        showEvidence: false as const,
        active: 'summary' as const,
        onChange: () => {},
      }
    : evidenceOnly
      ? {
          showSummary: false as const,
          showEvidence: true as const,
          active: 'evidence' as const,
          onChange: () => {},
        }
      : {
          showSummary: true as const,
          showEvidence: true as const,
          active: activePanel,
          onChange: setActivePanel,
        };

  return (
    <div className="drill-down-mapping-view">
      {contextItems.length > 0 && (
        <div className="titEtiqueta pt-4">
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
            {contextItems.map((item, index) => {
              const isGene = item.filterParam === 'gene_ncbi_id';
              const geneNcbiId =
                isGene && item.filterValue != null && String(item.filterValue).trim() !== ''
                  ? String(item.filterValue).trim()
                  : String(item.id ?? '').trim();
              return (
                <React.Fragment key={`${geneNcbiId}-${item.name ?? index}`}>
                  {index > 0 && <span className="px-1">/</span>}
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {item.name}
                    {isGene && onOpenEntityInfo && (
                      <MappedGeneInfoButton
                        gene={{ id: geneNcbiId, name: item.name ?? '' }}
                        onOpenPanel={onOpenEntityInfo}
                      />
                    )}
                  </span>
                </React.Fragment>
              );
            })}
          </span>
        </div>
      )}

      <MappingDrillDownProvider useDrillDown={true} effectiveTab={mappingContent.tabSegment?.toUpperCase()}>
        {!evidenceOnly && (summaryOnly || activePanel === 'summary') && (
          <div className="drill-down-mapping-summary">
            <ManageGrid
              key={`${keyBase}-summary`}
              view={view}
              source={source}
              tab={tab}
              panelTab={panelTab}
              searchItems={searchItemsMapped}
              setSearchItems={setSearchItems}
              isEvidence={false}
              isMapping={true}
              isInfoSidebarOpen={isInfoSidebarOpen}
              setIsInfoSidebarOpen={setIsInfoSidebarOpen}
              mappingContent={manageGridMappingContent}
              dispatch={dispatch}
              restrictChemical={false}
              showRightSidebar={false}
              summaryEvidenceToggle={drilldownSummaryEvidenceToggle}
              {...(summaryColumnModel != null && {
                columnVisibilityModel: summaryColumnModel,
                columnOrder: summaryColumnOrder,
                setColumnVisibilityModel: handleSetSummaryColumnVisibilityModel,
                setColumnOrder: handleSetSummaryColumnOrder,
              })}
            />
          </div>
        )}
        {!summaryOnly && (evidenceOnly || activePanel === 'evidence') && (
          <div className="drill-down-mapping-evidence">
            <ManageGrid
              key={`${keyBase}-evidence`}
              view={view}
              source={source}
              tab={tab}
              panelTab={panelTab}
              searchItems={searchItemsMapped}
              setSearchItems={setSearchItems}
              isEvidence={true}
              isMapping={true}
              isInfoSidebarOpen={isInfoSidebarOpen}
              setIsInfoSidebarOpen={setIsInfoSidebarOpen}
              mappingContent={manageGridMappingContent}
              dispatch={dispatch}
              restrictChemical={false}
              showRightSidebar={false}
              defaultFilterValue={defaultEvidenceFilter}
              summaryEvidenceToggle={drilldownSummaryEvidenceToggle}
              {...(evidenceColumnModel != null && {
                columnVisibilityModel: evidenceColumnModel,
                columnOrder: evidenceColumnOrder,
                setColumnVisibilityModel: handleSetEvidenceColumnVisibilityModel,
                setColumnOrder: handleSetEvidenceColumnOrder,
              })}
            />
          </div>
        )}
      </MappingDrillDownProvider>
    </div>
  );
}

export interface DrillDownTableContainerProps {
  baseLevelId: string;
  baseLabel: string;
  children: React.ReactNode;
  dispatch: any;
  panelTab?: string;
  onOpenEntityInfo?: (item: any) => void;
  isMappingUrlHydrated?: boolean;
}

export function DrillDownTableContainer({
  baseLevelId,
  baseLabel,
  children,
  dispatch,
  panelTab,
  onOpenEntityInfo,
  isMappingUrlHydrated = true,
}: DrillDownTableContainerProps) {
  const { view, source, tab, setSearchItems, searchItems } = searchContext();
  const { stack, currentIndex } = useMappingStackBreadcrumb();
  const resetStack = useMappingStackStore(s => s.resetStack);
  const currentLevel = useCurrentMappingLevel();
  const { setState: setMappingSidebarState } = useMappingSidebarColumn();
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = React.useState(false);
  const baseLevelRef = useRef<MappingLevel | null>(null);

  useEffect(() => {
    if (panelTab !== tab) return;

    const hasMappingInUrl = new URLSearchParams(window.location.search).has(MAPPING_URL_PARAM);

    const baseLevel: MappingLevel = {
      id: baseLevelId,
      label: baseLabel,
      isBase: true,
      searchItems: searchItems ?? [],
      panelTab,
      isEvidence: false,
    };
    baseLevelRef.current = baseLevel;
    const { stack } = useMappingStackStore.getState();
    if (hasMappingInUrl && stack.length > 1) return;
    const shouldReset =
      stack.length === 0 ||
      !stack[0]?.isBase ||
      (stack.length === 1 && stack[0].isBase && stack[0].id !== baseLevelId);
    if (shouldReset) {
      resetStack(baseLevel);
    }
  }, [baseLevelId, baseLabel, panelTab, tab, resetStack]);

  const isBase = currentIndex === 0;
  const level = currentLevel ?? (stack[0] ? stack[0] : null);

  if (!isMappingUrlHydrated) {
    return <div className="drill-down-table-container">{children}</div>;
  }

  return (
    <div className="drill-down-table-container">
      <MappingBreadcrumb className="mt-3 mb-2" />
      {isBase && <>{children}</>}
      {!isBase && level?.mappingContent && (
        <DrillDownMappingView
          level={level}
          view={view}
          source={source}
          tab={tab}
          panelTab={panelTab}
          setSearchItems={setSearchItems}
          isInfoSidebarOpen={isInfoSidebarOpen}
          setIsInfoSidebarOpen={setIsInfoSidebarOpen}
          dispatch={dispatch}
          registerSidebarColumnState={setMappingSidebarState}
          onOpenEntityInfo={onOpenEntityInfo}
        />
      )}
    </div>
  );
}

export default DrillDownTableContainer;
